import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  type HeadingTagType,
} from '@lexical/rich-text';
import {
  addExportVisitor$,
  addImportVisitor$,
  isMdastHTMLNode,
  type LexicalVisitor,
  type MdastHTMLNode,
  type MdastImportVisitor,
  realmPlugin,
} from '@mdxeditor/editor';
import {
  $createParagraphNode,
  $isElementNode,
  $isParagraphNode,
  type ElementFormatType,
  type ElementNode,
  type ParagraphNode,
} from 'lexical';

import {
  createTextAlignStyleAttribute,
  isBlockAlignment,
  parseTextAlignFromStyle,
  type TextAlignment,
} from './utils';

const ALIGNED_BLOCK_TAGS = new Set([
  'p',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

type ExportVisitorParams = Parameters<
  NonNullable<LexicalVisitor['visitLexicalNode']>
>[0];

type ExportActions = ExportVisitorParams['actions'];

type ExportMdastParent = ExportVisitorParams['mdastParent'];

const exportAlignedBlock = (
  tag: string,
  format: TextAlignment,
  actions: ExportActions,
) => {
  actions.addAndStepInto('mdxJsxFlowElement', {
    name: tag,
    type: 'mdxJsxFlowElement',
    attributes: [createTextAlignStyleAttribute(format)],
  });
};

const exportAlignedParagraphMdast = (
  format: TextAlignment,
  lexicalNode: ParagraphNode,
  mdastParent: ExportMdastParent,
  actions: ExportActions,
) => {
  const jsxParagraph = {
    type: 'mdxJsxTextElement' as const,
    name: 'p',
    attributes: [createTextAlignStyleAttribute(format)],
    children: [],
  };

  type MdastChild = ExportMdastParent['children'][number];

  const styledParagraph = jsxParagraph as Extract<
    MdastChild,
    { type: 'mdxJsxTextElement' }
  >;

  if (mdastParent.type === 'paragraph') {
    actions.appendToParent(mdastParent, styledParagraph);
    actions.visitChildren(lexicalNode, styledParagraph);
    return;
  }

  actions.appendToParent(mdastParent, {
    type: 'paragraph',
    children: [styledParagraph],
  } as MdastChild);
  actions.visitChildren(lexicalNode, styledParagraph);
};

const createAlignedBlockExportVisitor = <T extends ElementNode>(
  testLexicalNode: (
    node: Parameters<NonNullable<LexicalVisitor['testLexicalNode']>>[0],
  ) => node is T,
  getTag: (lexicalNode: T) => string,
): LexicalVisitor => ({
  testLexicalNode: testLexicalNode as LexicalVisitor['testLexicalNode'],
  priority: 1,
  visitLexicalNode({ lexicalNode, actions }) {
    const node = lexicalNode as T;
    const format = node.getFormatType();

    if (!isBlockAlignment(format)) {
      actions.nextVisitor();
      return;
    }

    exportAlignedBlock(getTag(node), format, actions);
  },
});

const alignedParagraphExportVisitor: LexicalVisitor = {
  testLexicalNode: $isParagraphNode,
  priority: 1,
  visitLexicalNode({ lexicalNode, mdastParent, actions }) {
    if (!$isParagraphNode(lexicalNode)) {
      actions.nextVisitor();
      return;
    }

    const format = pickBlockAlignment(lexicalNode.getFormatType());

    if (!format) {
      actions.nextVisitor();
      return;
    }

    exportAlignedParagraphMdast(format, lexicalNode, mdastParent, actions);
  },
};

const pickBlockAlignment = (format: ElementFormatType): TextAlignment | null =>
  isBlockAlignment(format) ? format : null;

const alignedHeadingExportVisitor = createAlignedBlockExportVisitor(
  $isHeadingNode,
  (lexicalNode) => lexicalNode.getTag(),
);

const alignedQuoteExportVisitor = createAlignedBlockExportVisitor(
  $isQuoteNode,
  () => 'blockquote',
);

const getStyleAttributeValue = (mdastNode: MdastHTMLNode) => {
  const styleAttribute = mdastNode.attributes.find(
    (attribute) =>
      attribute.type === 'mdxJsxAttribute' && attribute.name === 'style',
  );

  return typeof styleAttribute?.value === 'string'
    ? styleAttribute.value
    : undefined;
};

const createAlignedLexicalNode = (tag: string, format: ElementFormatType) => {
  if (tag === 'blockquote') {
    return $createQuoteNode().setFormat(format);
  }

  if (tag === 'p') {
    return $createParagraphNode().setFormat(format);
  }

  if (tag.startsWith('h') && tag.length === 2) {
    return $createHeadingNode(tag as HeadingTagType).setFormat(format);
  }

  return null;
};

const BLOCK_TAGS_WITH_PARAGRAPH_WRAPPER = new Set([
  'p',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

const flattenBlockWrappers = (
  children: MdastHTMLNode['children'],
  blockTag: string,
) => {
  if (!BLOCK_TAGS_WITH_PARAGRAPH_WRAPPER.has(blockTag)) {
    return children;
  }

  if (
    children.length === 1 &&
    children[0].type === 'paragraph' &&
    'children' in children[0]
  ) {
    return children[0].children;
  }

  return children;
};

const importAlignedHtmlNode = ({
  mdastNode,
  lexicalParent,
  actions,
  alignment,
}: {
  mdastNode: MdastHTMLNode;
  lexicalParent: Parameters<
    NonNullable<MdastImportVisitor<MdastHTMLNode>['visitNode']>
  >[0]['lexicalParent'];
  actions: Parameters<
    NonNullable<MdastImportVisitor<MdastHTMLNode>['visitNode']>
  >[0]['actions'];
  alignment: TextAlignment;
}) => {
  const flattenedChildren = flattenBlockWrappers(
    mdastNode.children,
    mdastNode.name,
  );
  const mdastNodeWithFlattenedChildren = {
    ...mdastNode,
    children: flattenedChildren,
  };

  if (
    mdastNode.type === 'mdxJsxTextElement' &&
    $isParagraphNode(lexicalParent)
  ) {
    if (mdastNode.name === 'p') {
      lexicalParent.getWritable().setFormat(alignment);
      actions.visitChildren(mdastNodeWithFlattenedChildren, lexicalParent);
      return;
    }

    const replacementNode = createAlignedLexicalNode(mdastNode.name, alignment);

    if (!replacementNode) {
      actions.nextVisitor();
      return;
    }

    lexicalParent.replace(replacementNode);
    actions.visitChildren(mdastNodeWithFlattenedChildren, replacementNode);
    return;
  }
  const lexicalNode = createAlignedLexicalNode(mdastNode.name, alignment);

  if (!lexicalNode || !$isElementNode(lexicalParent)) {
    actions.nextVisitor();
    return;
  }

  lexicalParent.append(lexicalNode);
  actions.visitChildren(mdastNodeWithFlattenedChildren, lexicalNode);
};

const alignedHtmlImportVisitor: MdastImportVisitor<MdastHTMLNode> = {
  priority: 1,
  testNode: (mdastNode) => {
    if (!isMdastHTMLNode(mdastNode)) {
      return false;
    }

    if (!ALIGNED_BLOCK_TAGS.has(mdastNode.name)) {
      return false;
    }

    return !!parseTextAlignFromStyle(getStyleAttributeValue(mdastNode));
  },
  visitNode({ mdastNode, lexicalParent, actions }) {
    const alignment = parseTextAlignFromStyle(
      getStyleAttributeValue(mdastNode),
    );

    if (!alignment) {
      actions.nextVisitor();
      return;
    }

    importAlignedHtmlNode({
      mdastNode,
      lexicalParent,
      actions,
      alignment,
    });
  },
};

export const textAlignmentPlugin = realmPlugin({
  init(realm) {
    realm.pub(addExportVisitor$, [
      alignedParagraphExportVisitor,
      alignedHeadingExportVisitor,
      alignedQuoteExportVisitor,
    ]);
    realm.pub(addImportVisitor$, alignedHtmlImportVisitor);
  },
});
